import type {
  CadCheck,
  ConfigurationResult,
  ConfigurationService,
  EquipmentConfiguration,
  EquipmentKind,
  EngineeringOverride,
  EngineeringWarning,
  ProjectSpecification,
  BuildSpecification,
  ConfigurationStatus,
  OnPipeAssumptions,
  OnPipeCatalogue,
} from "../models/types";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

let mockAssumptions: OnPipeAssumptions = {
  blastFrame: {
    smallHeadMaxSteelOdMm: 323.84,
    smallHeadSealChordMm: 85,
    largeHeadSealChordMm: 134.02,
    twoTieBarHeadPlateWidthMm: 222,
    fourTieBarHeadPlateWidthMm: 121,
    chamferSideClearanceMm: 25,
    cwcOverallLengthMarginMm: 10,
  },
  inductionCoil: {
    coatingClearanceMm: 23,
    preferredNonCwcVersionId: "V8",
    stepDownCoatingThicknessMm: 66,
    chamferClearanceMm: 50,
    cwcLengthMarginMm: 10,
  },
};

let mockCatalogue: OnPipeCatalogue = {
  datasetRevision: "mock-dataset",
  blastFrame: {
    ruleVersion: "BF-1.2",
    frameSizes: [],
    versions: [],
    kites: [],
    carriageRows: [],
  },
  inductionCoil: {
    ruleVersion: "IC-1.1",
    frames: [],
    versions: [],
  },
};

/** Demo-only fixtures so the UI can show status variants. Not engineering rules. */

const toMm = (value: string, unit: string) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return NaN;
  if (unit === "in") return n * 25.4;
  if (unit === "mil") return n * 0.0254;
  return n;
};

const diameterCategoryForOd = (odMm: number) => {
  if (odMm < 400) return "01";
  if (odMm < 600) return "02";
  if (odMm < 900) return "03";
  if (odMm < 1200) return "04";
  return "05";
};

const lengthCategoryForMm = (lengthMm: number) => {
  if (lengthMm < 12000) return "01";
  if (lengthMm < 18000) return "02";
  return "03";
};

const nextId = () => {
  const n = store.length + 1;
  return `CFG-${String(n).padStart(4, "0")}`;
};

const store: ConfigurationResult[] = [];

function overallStatus(odMm: number, wtMm: number): ConfigurationStatus {
  if (!Number.isFinite(odMm) || odMm <= 0) return "Error";
  if (odMm > 1100 || wtMm > 30) return "CAD Required";
  if (odMm > 900 || wtMm > 22) return "Review Required";
  return "Configured";
}

function mockWarnings(status: ConfigurationStatus, odMm: number): EngineeringWarning[] {
  if (status === "Error") {
    return [
      {
        level: "error",
        title: "No compatible equipment configuration",
        message:
          "No compatible equipment configuration was found for the supplied specification. Engineering review is required.",
      },
    ];
  }
  if (status === "CAD Required") {
    return [
      {
        level: "warning",
        title: "Engineering Review Required",
        message: "Final frame dimension requires CAD confirmation before manufacturing release.",
      },
    ];
  }
  if (status === "Review Required") {
    return [
      {
        level: "warning",
        title: "Engineering Review Required",
        message: `Pipe OD ${odMm.toFixed(0)} mm is near a category boundary and should be reviewed before release.`,
      },
    ];
  }
  return [
    {
      level: "approved",
      title: "Configured",
      message: "A compatible Blast Frame and Induction Coil configuration was generated.",
    },
  ];
}

function blastFrame(
  odMm: number,
  lengthMm: number,
  wtMm: number,
  spec: ProjectSpecification,
  status: ConfigurationStatus,
): EquipmentConfiguration {
  const diameterCategory = diameterCategoryForOd(odMm);
  const lengthCategory = lengthCategoryForMm(lengthMm);
  const configurationId = `BF-${diameterCategory}-${lengthCategory}`;
  const cadRequired = status === "CAD Required";
  return {
    kind: "blast-frame",
    configurationId,
    diameterCategory,
    lengthCategory,
    status: status === "Error" ? "Error" : "Configured",
    ruleVersion: "BF-1.2",
    keyDimensions: [
      { label: "Pipe OD", value: `${odMm.toFixed(1)} mm` },
      { label: "Wall thickness", value: `${wtMm.toFixed(1)} mm` },
      { label: "Frame envelope", value: `${(odMm + 80).toFixed(0)} mm` },
    ],
    features: [
      { name: "Frame A — diameter envelope", required: true, selected: true },
      { name: "Frame B — length envelope", required: true, selected: true },
      { name: "Frame C — blast path clearance", required: true, selected: true },
      { name: "Optional walkway kit", required: false, selected: odMm >= 900 },
    ],
    options: [
      { name: "Round Kite", status: "Selected" },
      { name: "Heavy-duty rollers", status: odMm >= 900 ? "Selected" : "Not Selected" },
      { name: "Extended blast hood", status: "Selected" },
      { name: "CAD-verified mounting", status: cadRequired ? "Review Required" : "Not Selected" },
    ],
    calculations: [
      { label: "Frame Size", value: `${diameterCategory}` },
      { label: "Version", value: `V${lengthCategory}` },
      { label: "Blast Head Size", value: odMm > 323.84 ? "Large Heads" : "Small Heads" },
      { label: "Carriage / Kite Type", value: "Round Kite" },
      {
        label: "CWC Config?",
        value: Number(spec.process.coatingThickness || 0) > 0 ? "Yes" : "No",
      },
      { label: "Pipe OD", value: `${odMm.toFixed(1)} mm` },
      { label: "Effective OD", value: `${(odMm + Number(spec.pipe.pipeTolerance || 0)).toFixed(1)} mm` },
      { label: "Calculated envelope", value: `${(odMm + 80).toFixed(0)} mm` },
    ],
    decisions: [
      {
        question: `Why was Diameter Category ${diameterCategory} selected?`,
        input: `Pipe OD = ${odMm.toFixed(1)} mm`,
        rule: `Pipe OD falls within Category ${diameterCategory} range.`,
        result: `Diameter Category = ${diameterCategory}`,
        ruleVersion: "BF-1.2",
      },
    ],
    warnings: mockWarnings(status, odMm),
    cadChecks: cadRequired
      ? [
          { id: "clearance", label: "Confirm frame clearance", confirmed: false },
          { id: "mounting", label: "Confirm final mounting dimension", confirmed: false },
          { id: "interface", label: "Confirm interface geometry", confirmed: false },
        ]
      : [],
    overrides: [],
    notes: "UI mock values only — not engineering-certified.",
  };
}

function inductionCoil(
  odMm: number,
  spec: ProjectSpecification,
  status: ConfigurationStatus,
): EquipmentConfiguration {
  const diameterCategory = diameterCategoryForOd(odMm);
  const configurationId = `IC-${diameterCategory}`;
  return {
    kind: "induction-coil",
    configurationId,
    diameterCategory,
    status: status === "Error" ? "Error" : "Configured",
    ruleVersion: "IC-1.1",
    keyDimensions: [
      { label: "Pipe OD", value: `${odMm.toFixed(1)} mm` },
      { label: "Coil ID allowance", value: `${(odMm + 40).toFixed(0)} mm` },
    ],
    features: [
      { name: "Coil A — diameter match", required: true, selected: true },
      { name: "Coil B — heating coverage", required: true, selected: true },
      { name: "Optional extra turns", required: false, selected: odMm >= 900 },
    ],
    options: [
      { name: "High-frequency kit", status: spec.process.heatingRequirement ? "Selected" : "Not Selected" },
      { name: "Extended cable set", status: "Selected" },
    ],
    calculations: [
      { label: "Frame Size", value: diameterCategory },
      { label: "Bottom End Version", value: "V8" },
      {
        label: "CWC Config?",
        value: Number(spec.process.coatingThickness || 0) > 0 ? "Yes" : "No",
      },
      { label: "Step-Down Config?", value: "No" },
      { label: "Rule Version", value: "IC-1.1" },
      { label: "Pipe OD", value: `${odMm.toFixed(1)} mm` },
      { label: "Effective OD", value: `${(odMm + 4).toFixed(1)} mm` },
      { label: "Coil category", value: diameterCategory },
    ],
    decisions: [
      {
        question: `Why was Diameter Category ${diameterCategory} selected?`,
        input: `Pipe OD = ${odMm.toFixed(1)} mm`,
        rule: `Pipe OD falls within Category ${diameterCategory} range.`,
        result: `Diameter Category = ${diameterCategory}`,
        ruleVersion: "IC-1.1",
      },
    ],
    warnings: mockWarnings(status, odMm).filter((w) => w.level !== "error"),
    cadChecks: [],
    overrides: [],
    notes: "UI mock values only — not engineering-certified.",
  };
}

function buildResult(
  spec: ProjectSpecification,
  existingId?: string,
): ConfigurationResult {
  const odMm = toMm(spec.pipe.outsideDiameter, spec.pipe.outsideDiameterUnit);
  const wtMm = toMm(spec.pipe.wallThickness, spec.pipe.wallThicknessUnit);
  const lengthMm = toMm(spec.pipe.pipeLength, spec.pipe.pipeLengthUnit) || 12000;
  const status = overallStatus(odMm, wtMm);
  const configurationId = existingId || nextId();
  const isError = status === "Error";

  return {
    configurationId,
    revision: spec.project.projectRevision || "A",
    status,
    createdAt: new Date().toISOString(),
    project: spec.project,
    pipe: spec.pipe,
    process: spec.process,
    blastFrame: isError ? null : blastFrame(odMm, lengthMm, wtMm, spec, status),
    inductionCoil: isError ? null : inductionCoil(odMm, spec, status),
    warnings: mockWarnings(status, odMm),
    errorMessage: isError
      ? "No compatible equipment configuration was found for the supplied specification. Engineering review is required."
      : undefined,
  };
}

function toBuildSpec(result: ConfigurationResult, equipment: EquipmentKind): BuildSpecification {
  const eq = equipment === "blast-frame" ? result.blastFrame : result.inductionCoil;
  if (!eq) {
    throw new Error("Configuration cannot be automatically determined because required engineering data is unavailable.");
  }
  return {
    configurationId: result.configurationId,
    revision: result.revision,
    status: result.status,
    project: result.project,
    equipment,
    equipmentLabel: equipment === "blast-frame" ? "Blast Frame" : "Induction Coil",
    equipmentConfigurationId: eq.configurationId,
    classification: [
      ...(equipment === "blast-frame"
        ? [{ label: "Frame Size", value: eq.diameterCategory }]
        : [{ label: "Diameter Category", value: eq.diameterCategory }]),
      ...(eq.lengthCategory ? [{ label: "Length Category", value: eq.lengthCategory }] : []),
    ],
    requiredFeatures: eq.features.filter((f) => f.required && f.selected).map((f) => f.name),
    selectedOptions: eq.options.filter((o) => o.status === "Selected").map((o) => o.name),
    engineeringParameters: eq.keyDimensions,
    cadRequirements: eq.cadChecks.length ? "Review required" : "None",
    engineeringStatus: result.status.toUpperCase(),
  };
}

export const mockConfigurationService: ConfigurationService = {
  async listConfigurations() {
    await wait(200);
    return [...store].reverse();
  },

  async createConfiguration(project) {
    await wait(1400);
    const result = buildResult(project);
    store.push(result);
    return result;
  },

  async updateConfiguration(configurationId, project) {
    await wait(1400);
    const index = store.findIndex((item) => item.configurationId === configurationId);
    if (index < 0) throw new Error("Configuration not found.");
    const previous = store[index];
    const result = {
      ...buildResult(project, configurationId),
      createdAt: previous.createdAt,
    };
    store[index] = result;
    return result;
  },

  async deleteConfiguration(configurationId) {
    await wait(200);
    const index = store.findIndex((item) => item.configurationId === configurationId);
    if (index < 0) throw new Error("Configuration not found.");
    store.splice(index, 1);
  },

  async getConfiguration(configurationId) {
    await wait(180);
    const found = store.find((item) => item.configurationId === configurationId);
    if (!found) throw new Error("Configuration not found.");
    return found;
  },

  async getBuildSpecification(configurationId, equipment) {
    await wait(180);
    const found = store.find((item) => item.configurationId === configurationId);
    if (!found) throw new Error("Configuration not found.");
    return toBuildSpec(found, equipment);
  },

  async updateCadChecks(configurationId, equipment, checks) {
    await wait(200);
    const found = store.find((item) => item.configurationId === configurationId);
    if (!found) throw new Error("Configuration not found.");
    const target = equipment === "blast-frame" ? found.blastFrame : found.inductionCoil;
    if (target) target.cadChecks = checks;
    return found;
  },

  async applyOverride(configurationId, equipment, override) {
    await wait(200);
    const found = store.find((item) => item.configurationId === configurationId);
    if (!found) throw new Error("Configuration not found.");
    const target = equipment === "blast-frame" ? found.blastFrame : found.inductionCoil;
    if (target) {
      target.overrides = [...target.overrides.filter((o) => o.field !== override.field), override];
    }
    return found;
  },

  async getCatalogue() {
    await wait(200);
    return structuredClone(mockCatalogue);
  },

  async createBlastFrameFrameSize(item) {
    await wait(200);
    mockCatalogue.blastFrame.frameSizes = [...mockCatalogue.blastFrame.frameSizes, item];
    return { ...item };
  },

  async createBlastFrameVersion(item) {
    await wait(200);
    mockCatalogue.blastFrame.versions = [...mockCatalogue.blastFrame.versions, item];
    return { ...item };
  },

  async createBlastFrameKite(item) {
    await wait(200);
    mockCatalogue.blastFrame.kites = [...mockCatalogue.blastFrame.kites, item];
    return { ...item };
  },

  async createBlastFrameCarriageRow(item) {
    await wait(200);
    mockCatalogue.blastFrame.carriageRows = [...(mockCatalogue.blastFrame.carriageRows || []), item];
    return { ...item };
  },

  async deleteBlastFrameFrameSize(label) {
    await wait(200);
    mockCatalogue.blastFrame.frameSizes = mockCatalogue.blastFrame.frameSizes.filter(
      (row) => row.label !== label,
    );
  },

  async deleteBlastFrameVersion(versionId) {
    await wait(200);
    mockCatalogue.blastFrame.versions = mockCatalogue.blastFrame.versions.filter(
      (row) => row.id !== versionId,
    );
  },

  async deleteBlastFrameKite(kiteId) {
    await wait(200);
    const [id, heads] = kiteId.split("|");
    mockCatalogue.blastFrame.kites = mockCatalogue.blastFrame.kites.filter(
      (row) => !(row.id === id && row.heads === heads),
    );
  },

  async deleteBlastFrameCarriageRow(frameSize) {
    await wait(200);
    mockCatalogue.blastFrame.carriageRows = (mockCatalogue.blastFrame.carriageRows || []).filter(
      (row) => row.frameSize !== frameSize,
    );
  },

  async createInductionCoilFrame(item) {
    await wait(200);
    mockCatalogue.inductionCoil.frames = [...mockCatalogue.inductionCoil.frames, item];
    return { ...item };
  },

  async createInductionCoilVersion(item) {
    await wait(200);
    mockCatalogue.inductionCoil.versions = [...mockCatalogue.inductionCoil.versions, item];
    return { ...item };
  },

  async deleteInductionCoilFrame(frameId) {
    await wait(200);
    const [nbInRaw, clearanceType, indexRaw] = frameId.split("|");
    const nbIn = Number(nbInRaw);
    const index = Number(indexRaw);
    mockCatalogue.inductionCoil.frames = mockCatalogue.inductionCoil.frames.filter(
      (row) =>
        !(row.nbIn === nbIn && row.clearanceType === clearanceType && row.index === index),
    );
  },

  async deleteInductionCoilVersion(versionId) {
    await wait(200);
    mockCatalogue.inductionCoil.versions = mockCatalogue.inductionCoil.versions.filter(
      (row) => row.id !== versionId,
    );
  },

  async getAssumptions() {
    await wait(200);
    return structuredClone(mockAssumptions);
  },

  async updateAssumptions(assumptions) {
    await wait(200);
    mockAssumptions = structuredClone(assumptions);
    return structuredClone(mockAssumptions);
  },
};
